import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  createSupplier,
  deactiveSupplier,
  editSupplier,
  getSupplier,
  getSuppliersByPage,
  reactiveSupplier,
} from '../../ApiServices/SupplierServices';
import { RootState } from '../reduxStore';
import { hideLoader, showLoader } from './loaderSlice';
import { showErrorSnackbar, showSuccessSnackbar } from './snackbarSlice';
import { t } from 'i18next';
import { handleResponse } from '../../ApiServices/ApiHandler';

export const initialState = {
  value: {
    totalCount: -1,
    data: [],
    cities: [''],
    states: [''],
    zipCodes: [''],
  },
  loading: false,
  error: null,
  rowsPerPage: 10,
  pageNo: 0,
  selectedValue: null,
  filter: {},
};

const suppliersSlice = createSlice({
  name: 'suppliers',
  initialState: initialState,
  reducers: {
    clearSupplierState(state: any) {
      state.rowsPerPage = initialState.rowsPerPage;
      state.pageNo = initialState.pageNo;
      state.value = initialState.value;
      state.loading = false;
      state.error = null;
      state.selectedValue = initialState.selectedValue;
      state.filter = initialState.filter;
    },
    clearPageNo(state: any) {
      state.pageNo = initialState.pageNo;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchSuppliers.fulfilled, (state: any, action: any) => {
      state.error = initialState.error;
      state.loading = initialState.loading;
      state.selectedValue = initialState.selectedValue;
      state.value = action?.payload?.data;
      state.pageNo = action?.meta?.arg?.page;
      state.rowsPerPage = action?.meta?.arg?.limit;
      state.filter = action?.meta?.arg;
    });
    builder.addCase(fetchSuppliers.rejected, (state: any, action: any) => {
      state.error = action?.payload?.error?.message;
      state.loading = false;
      state.selectedValue = initialState?.selectedValue;
    });
    builder.addCase(fetchSupplierById.fulfilled, (state: any, action: any) => {
      state.error = initialState.error;
      state.loading = initialState.loading;
      state.selectedValue = action?.payload?.data;
    });
    builder.addCase(fetchSupplierById.rejected, (state: any, action: any) => {
      state.loading = initialState.loading;
      state.selectedValue = initialState.selectedValue;
      state.error = action?.payload;
    });
  },
});

export const suppliersState = (state: RootState) => state?.suppliers;
export const { clearSupplierState, clearPageNo } = suppliersSlice.actions;
export default suppliersSlice.reducer;

export const fetchSuppliers = createAsyncThunk(
  'suppliers/fetchSuppliers',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('FETCHING_SUPPLIERS') }));
      return fulfillWithValue(await getSuppliersByPage(payload));
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const fetchSupplierById = createAsyncThunk(
  'suppliers/fetchSupplierById',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('FETCHING_SUPPLIER_DETAILS') }));
      return fulfillWithValue(await getSupplier(payload));
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const modifySupplier = createAsyncThunk(
  'suppliers/modifySupplier',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('MODIFYING_SUPPLIER_DETAILS') }));
      let response: any = await editSupplier(payload);
      if (response?.status === 200 || response === 200) {
        dispatch(
          showSuccessSnackbar({ message: t('SUPPLIER_UPDATED_SUCCESS') })
        );
        return fulfillWithValue(response);
      } else {
        dispatch(showErrorSnackbar({ message: t('SUPPLIER_UPDATE_FAILED') }));
        return rejectWithValue(response);
      }
      // return await handleResponse(
      //   response,
      //   fulfillWithValue,
      //   rejectWithValue,
      //   dispatch
      // );
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const addSupplier = createAsyncThunk(
  'suppliers/addSupplier',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('ADDING_NEW_SUPPLIER_DETAILS') }));
      let response: any = await createSupplier(payload);
      // return await handleResponse(
      //   response,
      //   fulfillWithValue,
      //   rejectWithValue,
      //   dispatch
      // );
      if (response?.status === 200 || response === 200) {
        dispatch(showSuccessSnackbar({ message: t('SUPPLIER_ADDED_SUCCESS') }));
        return fulfillWithValue(response);
      } else {
        dispatch(showErrorSnackbar({ message: t('ORDER_CREATION_FAILED') }));
        return rejectWithValue(response);
      }
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const reactivateSupplier = createAsyncThunk(
  'suppliers/reactivateSupplier',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('REACTIVATING_SUPPLIER') }));
      let response: any = await reactiveSupplier(payload);
      if (response?.status === 200 || response === 200) {
        dispatch(
          showSuccessSnackbar({ message: t('SUPPLIER_REACTIVATED_SUCCESS') })
        );
        return fulfillWithValue(response);
      } else {
        dispatch(
          showErrorSnackbar({ message: t('SUPPLIER_REACTIVATION_FAILED') })
        );
        return rejectWithValue(response);
      }
      // return await handleResponse(
      //   response,
      //   fulfillWithValue,
      //   rejectWithValue,
      //   dispatch
      // );
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const deactivateSupplier = createAsyncThunk(
  'suppliers/deactivateSupplier',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('DEACTIVATING_SUPPLIER') }));
      let response: any = await deactiveSupplier(payload);
      if (response?.status === 200 || response === 200) {
        dispatch(
          showSuccessSnackbar({ message: t('SUPPLIER_DEACTIVATED_SUCCESS') })
        );
        return fulfillWithValue(response);
      } else {
        dispatch(
          showErrorSnackbar({ message: t('SUPPLIER_DEACTIVATION_FAILED') })
        );
        return rejectWithValue(response);
      }
      //return await handleResponse(response, fulfillWithValue, rejectWithValue, dispatch);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const downloadSuppliers = createAsyncThunk(
  'suppliers/downloadSuppliers',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('DOWNLOADING_SUPPLIER_LIST') }));
      let response: any = await getSuppliersByPage(payload);
      response === 200
        ? dispatch(showSuccessSnackbar({ message: t('DOWNLOAD_SUCCESS') }))
        : dispatch(showErrorSnackbar({ message: t('DOWNLOAD_FAILURE') }));
      return fulfillWithValue(response);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const downloadSupplierDetails = createAsyncThunk(
  'suppliers/downloadSupplierDetails',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('DOWNLOADING_SUPPLIER_LIST') }));
      let response: any = await getSupplier(payload);
      response === 200
        ? dispatch(showSuccessSnackbar({ message: t('DOWNLOAD_SUCCESS') }))
        : dispatch(showErrorSnackbar({ message: t('DOWNLOAD_FAILURE') }));
      return fulfillWithValue(response);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);
